import type {
  DataRecord,
  DataValue,
  FieldFilter,
  LabelFilter,
  LabelQuery,
  MemberFilter,
  QueryFilter,
} from '../types';
import type { Dataset } from '../data/dataset';
import { toDate } from './dateParts';

/** A predicate over a single record. */
export type RecordPredicate = (record: DataRecord) => boolean;

function memberKey(value: DataValue): string {
  if (value == null) return ' null';
  if (value instanceof Date) return `d:${value.getTime()}`;
  return `${typeof value}:${String(value)}`;
}

function buildMemberPredicate(
  dataset: Dataset,
  uniqueName: string,
  filter: MemberFilter,
): RecordPredicate {
  if (filter.include && filter.include.length) {
    const allowed = new Set(filter.include.map(memberKey));
    return (record) => allowed.has(memberKey(dataset.resolveValue(record, uniqueName)));
  }
  if (filter.exclude && filter.exclude.length) {
    const blocked = new Set(filter.exclude.map(memberKey));
    return (record) => !blocked.has(memberKey(dataset.resolveValue(record, uniqueName)));
  }
  return () => true;
}

function buildLabelPredicate(
  dataset: Dataset,
  uniqueName: string,
  filter: LabelFilter,
): RecordPredicate {
  const test = labelMatcher(filter.query);
  return (record) => {
    const caption = dataset.memberCaption(uniqueName, dataset.resolveValue(record, uniqueName));
    return test(caption);
  };
}

function labelMatcher(query: LabelQuery): (caption: string) => boolean {
  const value = 'value' in query ? query.value : '';
  const lower = value.toLowerCase();
  switch (query.op) {
    case 'contains':
      return (c) => c.toLowerCase().includes(lower);
    case 'notContains':
      return (c) => !c.toLowerCase().includes(lower);
    case 'beginsWith':
      return (c) => c.toLowerCase().startsWith(lower);
    case 'endsWith':
      return (c) => c.toLowerCase().endsWith(lower);
    case 'equal':
      return (c) => c.toLowerCase() === lower;
    case 'notEqual':
      return (c) => c.toLowerCase() !== lower;
    case 'regex': {
      let re: RegExp;
      try {
        re = new RegExp(value);
      } catch {
        re = /$^/;
      }
      return (c) => re.test(c);
    }
    default:
      return () => true;
  }
}

function buildQueryPredicate(
  dataset: Dataset,
  uniqueName: string,
  filter: QueryFilter,
): RecordPredicate {
  const entries = Object.entries(filter.query);
  return (record) => {
    const value = dataset.resolveValue(record, uniqueName);
    const num = typeof value === 'number' ? value : Number(value);
    const date = toDate(value);
    return entries.every(([op, target]) => {
      const t = target as DataValue;
      const tn = typeof t === 'number' ? t : Number(t);
      switch (op) {
        case 'min':
        case 'greaterEqual':
          return num >= tn;
        case 'max':
        case 'lessEqual':
          return num <= tn;
        case 'greater':
          return num > tn;
        case 'less':
          return num < tn;
        case 'equal':
          return value === t;
        case 'after':
          return date != null && t != null && date.getTime() >= (toDate(t)?.getTime() ?? NaN);
        case 'before':
          return date != null && t != null && date.getTime() <= (toDate(t)?.getTime() ?? NaN);
        default:
          return true;
      }
    });
  };
}

/**
 * Build a single record predicate for a field filter. Value filters (top/bottom
 * N, threshold-by-measure) are *not* record-level and are handled later on the
 * axis after aggregation — they return a pass-through here.
 */
export function buildRecordPredicate(
  dataset: Dataset,
  uniqueName: string,
  filter: FieldFilter,
): RecordPredicate {
  switch (filter.type) {
    case 'members':
      return buildMemberPredicate(dataset, uniqueName, filter);
    case 'label':
      return buildLabelPredicate(dataset, uniqueName, filter);
    case 'query':
      return buildQueryPredicate(dataset, uniqueName, filter);
    case 'value':
    default:
      return () => true;
  }
}

/** Combine multiple field filters into one AND-ed predicate. */
export function combinePredicates(predicates: RecordPredicate[]): RecordPredicate {
  if (!predicates.length) return () => true;
  if (predicates.length === 1) return predicates[0]!;
  return (record) => predicates.every((p) => p(record));
}
